import { useMemo } from 'react'

// A Redux binding for Next.js Router compatible with Next.js.
import { createRouterMiddleware, initialRouterState } from 'connected-next-router'
import toArray from 'lodash/toArray'
import Router from 'next/router'
import { applyMiddleware, createStore as createReduxStore } from 'redux'
import thunkMiddleware from 'redux-thunk'
import reducer, { reduxMiddlewares } from 'reduxBits/rootReducer'
import Immutable from 'seamless-immutable'

/**
 * Bind redux store middleware https://redux.js.org/api/applymiddleware
 * if development ad redux devtools middleware
 */
const bindMiddleware = (middleware) => {
  const { composeWithDevTools } = require('redux-devtools-extension') // eslint-disable-line global-require
  return composeWithDevTools(applyMiddleware(...middleware))
}

// Saved store to be used between pages client side
let savedStore

// Create connected-next-router middkeware
// https://github.com/danielr18/connected-next-router#step-1
const routerMiddleware = createRouterMiddleware()
/**
 * Creates a redux store
 * https://redux.js.org/usage/configuring-your-store
 * @param  {} initialState a redux store state
 * @returns store
 */
const createStore = (initialState) => {
  const store = createReduxStore(
    reducer,
    Immutable.from(initialState),
    bindMiddleware([thunkMiddleware, ...toArray(reduxMiddlewares), routerMiddleware]),
  )
  return store
}
/**
 * Initialize app redux store
 * @param  {} preloadedState State to be loaded in to the sore
 * @param  { asPath } ctx page path to be used to initialize redux connected next router
 * @returns store
 */
export const initializeStore = (preloadedState, ctx) => {
  // Get next router asPath from Nextjs context or the Nextjs router instance
  const { asPath } = ctx || Router.router || {}
  const initialState = {
    ...preloadedState,
    router: initialRouterState(asPath),
  }
  // if savedStore is undefined, create new redux store
  let store = savedStore ?? createStore(initialState)

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (initialState && savedStore) {
    store = createStore({
      ...savedStore.getState(),
      ...initialState,
    })
    // Reset the current savedStore
    savedStore = undefined
  }

  // On the server always create a new store
  // Do not persist the store
  if (typeof window === 'undefined') {
    return store
  }

  // Save the store so that we can get the state later if user navigates
  if (!savedStore) {
    savedStore = store
  }

  return store
}

export function useStore(initialState) {
  const store = useMemo(() => initializeStore(initialState), [initialState])
  return store
}
